import { useMemo } from "react";
import type { Session } from "@tellescope/sdk";
import type { AccessAction, AccessType } from "@tellescope/types-models";
import type { ModelName, StandardQueryMethodForModel, SupportedSession } from "../../../types";
import type { TellescopeModelFromName } from "../../../mappings";
import { useTellescopeSession } from "../../../context";
import { useTellescopeQuery } from "../../use-tellescope-query";
import { useAuth } from "../../../auth";
import { ACCESS_EVALUATORS, type ModelWithAccessEvaluator } from "./access-evaluators";
import { useAccessibleEnduserIds } from "./use-accessible-endusers";
import type { PermissionResultsByAction } from "./types";

/**
 * Models that expose a `getOne` standard query method. The hook is constrained
 * to these so the optional internal record fetch (`recordId`) is type-safe.
 */
export type ModelWithGetOne = {
  [K in ModelName]: "getOne" extends StandardQueryMethodForModel<K> ? K : never;
}[ModelName];

/**
 * Models accepted by `usePermissionQuery`: those with a registered access
 * evaluator AND a `getOne` query method (for the optional internal record fetch).
 */
export type UsePermissionQueryModel = ModelWithAccessEvaluator & ModelWithGetOne;

export interface UsePermissionQueryArgs<Model extends UsePermissionQueryModel, Actions extends AccessAction> {
  model: Model;
  actions: readonly Actions[];
  /**
   * The record to evaluate permissions against. If omitted (and no `recordId`
   * is provided), the hook performs a capability check based purely on the
   * session's access type for each action.
   */
  record?: TellescopeModelFromName<Model>;
  /**
   * Optional record ID. When provided and `record` is not, the hook fetches
   * the record via `useTellescopeQuery({ model, method: "getOne" })`.
   */
  recordId?: string;
  /**
   * The `access` value reported for each action while the `recordId` fetch is
   * in flight. Defaults to `true` (fail open) so UI the user may be entitled to
   * isn't hidden while loading — the backend remains the authoritative
   * gatekeeper. Pass `false` for the rare consumer that wants to fail closed
   * during load. `type` is reported as resolved, and an action with a `null`
   * access type always reports `access: false`.
   */
  accessWhileLoading?: boolean;
  session?: SupportedSession;
}

const isUserSession = (session: SupportedSession): session is Session => "access" in session.userInfo;

export const resolveAccessTypesForActions = <Actions extends AccessAction>(
  session: SupportedSession | null,
  model: ModelName,
  actions: readonly Actions[]
): Record<Actions, AccessType> => {
  const isUser = !!session && isUserSession(session);
  // Super-admins (`isa`) bypass all row filtering on the backend
  // (`modifyFilterForAccessConstraint`), independent of the `Admin` role. The
  // role-derived `access` map may sit below `All` for them, so honoring it
  // directly would hide UI the backend would allow — treat `isa` as `All`.
  const isSuperAdmin = isUser && !!session.userInfo.isa;
  const map = actions.reduce<Partial<Record<Actions, AccessType>>>((acc, action) => {
    acc[action] = isSuperAdmin ? "All" : isUser ? session.userInfo.access[model]?.[action] ?? null : null;
    return acc;
  }, {});
  // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
  return map as Record<Actions, AccessType>;
};

interface ResolvePermissionsParams<Model extends ModelWithAccessEvaluator, Actions extends AccessAction> {
  model: Model;
  actions: readonly Actions[];
  accessTypes: Record<Actions, AccessType>;
  record: TellescopeModelFromName<Model> | undefined;
  currentUserId: string;
  /**
   * Whether the record is still loading (the `recordId` fetch). While `true`,
   * each non-`null` action reports {@link accessWhileLoading} instead of an
   * evaluated result, so the hook never resolves a record-dependent decision
   * against a not-yet-loaded record.
   */
  isLoading?: boolean;
  /**
   * `access` reported for non-`null` actions while {@link isLoading}. Defaults
   * to `true` (fail open), matching the {@link usePermissionQuery} policy so a
   * direct caller of this util fails open during a fetch rather than silently
   * hiding entitled UI.
   */
  accessWhileLoading?: boolean;
  /**
   * The record's related enduser IDs the current user can access (returned by
   * the access-restricted endusers read). Forwarded to the evaluator so
   * "Assigned" grants accurately. Defaults to empty (→ "Assigned" denies),
   * which is correct for records with no enduser linkage.
   */
  accessibleRelatedEnduserIds?: Set<string>;
  /**
   * The current user's `tags`, forwarded to the evaluator for models with an
   * accessTags grant (e.g. `endusers`). The caller folds the org's
   * `enableAccessTags` gate into this (empty when off); evaluators that don't
   * use access tags ignore it. Defaults to empty.
   */
  currentUserTags?: readonly string[];
}

export const resolvePermissionsForRecord = <Model extends ModelWithAccessEvaluator, Actions extends AccessAction>({
  model,
  actions,
  accessTypes,
  record,
  currentUserId,
  isLoading = false,
  accessWhileLoading = true,
  accessibleRelatedEnduserIds = new Set<string>(),
  currentUserTags = [],
}: ResolvePermissionsParams<Model, Actions>): PermissionResultsByAction<Actions> => {
  const evaluator = ACCESS_EVALUATORS[model];
  const result = actions.reduce<Partial<PermissionResultsByAction<Actions>>>((acc, action) => {
    const type = accessTypes[action];

    if (type === null) {
      acc[action] = { access: false, type: null };
    } else if (isLoading) {
      acc[action] = { access: accessWhileLoading, type };
    } else if (!record) {
      // Fail-open capability check: with no record (none provided, or a
      // `recordId` that resolved to nothing / is forbidden), grant and let the
      // backend gatekeep. `null` access types are already denied above.
      acc[action] = { access: true, type };
    } else {
      acc[action] = {
        access: evaluator.evaluate({
          record,
          currentUserId,
          accessType: type,
          accessibleRelatedEnduserIds,
          currentUserTags,
        }),
        type,
      };
    }
    return acc;
  }, {});

  // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
  return result as PermissionResultsByAction<Actions>;
};

/**
 * Hook for evaluating one or more permission actions against a single record.
 *
 * Behavior:
 * - For each action in `actions`, reads the session's access type from
 *   `userInfo.access[model][action]`.
 * - If a `record` is provided (or `recordId` resolves to a fetched record),
 *   evaluates per-record access via the model's registered evaluator.
 * - If neither `record` nor `recordId` is provided, returns a capability
 *   check: `access` is true whenever the access type is non-null.
 *
 * When any action resolves to `Assigned` access and the record is
 * enduser-linked, the hook additionally issues the access-restricted
 * `endusers.getByIds` read to determine which related endusers the user can
 * reach, so `Assigned` is granted accurately rather than for every
 * enduser-linked record.
 *
 * While either the `recordId` fetch or the `Assigned` enduser fetch is in
 * flight, every non-`null` action reports `accessWhileLoading` (default `true`,
 * fail open). Consumers that need a settled decision should gate on `isLoading`;
 * pass `accessWhileLoading: false` to fail closed during load.
 *
 * @returns An object keyed by each requested action, each containing
 *   `{ access, type }`, plus an aggregate `isLoading` flag covering the internal
 *   record fetch (when `recordId` was provided) and the `Assigned` enduser fetch.
 */
export const usePermissionQuery = <Model extends UsePermissionQueryModel, Actions extends AccessAction>({
  model,
  actions,
  record,
  recordId,
  accessWhileLoading = true,
  session: providedSession,
}: UsePermissionQueryArgs<Model, Actions>): PermissionResultsByAction<Actions> & { isLoading: boolean } => {
  const session = useTellescopeSession(providedSession);
  const { currentUser, selectedOrganization } = useAuth();

  const accessTypes = useMemo(() => resolveAccessTypesForActions(session, model, actions), [actions, model, session]);

  // The accessTags grant only applies when the org enables it (`session.eat`);
  // fold that gate into the tags here so the evaluator just checks intersection.
  const currentUserTags = useMemo(
    () => (selectedOrganization?.settings?.endusers?.enableAccessTags ? currentUser.tags ?? [] : []),
    [currentUser.tags, selectedOrganization?.settings?.endusers?.enableAccessTags]
  );

  const recordFetchEnabled = !record && !!recordId;
  const fetchedRecordQuery = useTellescopeQuery({
    model,
    method: "getOne",
    // @ts-expect-error -- request type widens with the un-narrowable Method
    request: recordId ?? "",
    enabled: recordFetchEnabled,
    session: providedSession,
  });
  // @ts-expect-error -- data widens to `unknown` for the un-narrowable generic Model
  const effectiveRecord: TellescopeModelFromName<Model> | undefined = record ?? fetchedRecordQuery.data;

  const recordFetchLoading = recordFetchEnabled && fetchedRecordQuery.isLoading;

  const relatedEnduserIds = useMemo(
    () =>
      effectiveRecord
        ? ACCESS_EVALUATORS[model].getRelatedEnduserIds?.(effectiveRecord) ?? new Set<string>()
        : new Set<string>(),
    [effectiveRecord, model]
  );
  const anyAssigned = useMemo(() => Object.values<AccessType>(accessTypes).includes("Assigned"), [accessTypes]);

  const enduserIds = useMemo(() => [...relatedEnduserIds], [relatedEnduserIds]);
  const { accessibleEnduserIds, isLoading: enduserFetchLoading } = useAccessibleEnduserIds({
    enduserIds,
    enabled: anyAssigned,
    session: providedSession,
  });

  const accessibleRelatedEnduserIds = useMemo(
    () => new Set([...relatedEnduserIds].filter((id) => accessibleEnduserIds.has(id))),
    [accessibleEnduserIds, relatedEnduserIds]
  );

  const isLoading = recordFetchLoading || enduserFetchLoading;

  const permissions = useMemo(
    () =>
      resolvePermissionsForRecord<Model, Actions>({
        model,
        actions,
        accessTypes,
        record: effectiveRecord,
        currentUserId: currentUser.id,
        isLoading,
        accessWhileLoading,
        accessibleRelatedEnduserIds,
        currentUserTags,
      }),
    [
      accessTypes,
      accessWhileLoading,
      accessibleRelatedEnduserIds,
      actions,
      currentUser.id,
      currentUserTags,
      effectiveRecord,
      isLoading,
      model,
    ]
  );

  return useMemo(() => ({ ...permissions, isLoading }), [isLoading, permissions]);
};
